import React, { useContext, useState, useEffect, useReducer, useRef, memo, useCallback } from "react";
import { isSameDay, parseISO, format } from "date-fns";
import clsx from "clsx";
import { isNil } from "lodash";
import { blue, green, red } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
  Badge
} from "@material-ui/core";

import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  Facebook,
  Instagram,
  Reply,
  Close,
  Translate,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark.png";
import YouTubePreview from "../ModalYoutubeCors";

import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";

import { Popper, Paper, ClickAwayListener } from "@material-ui/core";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import api from "../../services/api";
import toastError from "../../errors/toastError";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";
import SelectMessageCheckbox from "./SelectMessageCheckbox";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import { QueueSelectedContext } from "../../context/QueuesSelected/QueuesSelectedContext";
import AudioModal from "../AudioModal";
import AdMetaPreview from "../AdMetaPreview"; // Adicionado componente de preview de anúncio
import SaveFieldFromTextMenu from "../SaveFieldFromTextMenu";

import { messages } from "../../translate/languages";
import { useParams, useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200,
  },

  saveFieldFab: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderRadius: 20,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
    zIndex: 9999,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },

  currentTick: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "95%",
    backgroundColor: theme.palette.primary.main,
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "1px 5px 10px #b3b3b3",
  },

  currentTicktText: {
    color: theme.palette.primary,
    fontWeight: 'bold',
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  messagesList: {
    backgroundImage: theme.mode === 'light' ? `url(${whatsBackground})` : `url(${whatsBackgroundDark})`,
    backgroundColor: theme.mode === 'light' ? "transparent" : "#0b0b0d",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 30px 20px",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  dragElement: {
    background: 'rgba(255, 255, 255, 0.8)',
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: 999999,
    textAlign: "center",
    fontSize: "3em",
    border: "5px dashed #333",
    color: '#333',
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  circleLoading: {
    color: blue[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.mode === 'light' ? "#ffffff" : "#202c33",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.mode === 'light' ? "#f0f0f0" : "#1d282f",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#388aff",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: theme.mode === 'light' ? "#dcf8c6" : "#005c4b",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  messageRightPrivate: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: "#F0E68C",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.mode === 'light' ? "#cfe9ba" : "#025144",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: 400,
    height: "auto",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
  },

  forwardMessage: {
    fontSize: 12,
    fontStyle: "italic",
    position: "absolute",
    top: 0,
    left: 5,
    color: "#999",
    display: "flex",
    alignItems: "center"
  },

  translatedIndicator: {
    fontSize: 11,
    color: "#667781",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 4,
    padding: "3px 0 1px",
    borderTop: "1px solid rgba(0,0,0,0.06)",
    userSelect: "none",
    "&:hover": {
      textDecoration: "underline",
    }
  },

  translatedIcon: {
    fontSize: 14,
  },

  transcribeBtn: {
    fontSize: 11,
    color: "#667781",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 4,
    padding: "3px 0 1px",
    userSelect: "none",
    "&:hover": {
      textDecoration: "underline",
    }
  },

  transcriptionText: {
    fontSize: 12,
    color: "#303030",
    marginTop: 4,
    padding: "6px 8px",
    backgroundColor: "rgba(0,0,0,0.04)",
    borderRadius: 6,
    lineHeight: 1.4,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },

  transcriptionLang: {
    fontSize: 10,
    color: "#667781",
    marginTop: 2,
    fontStyle: "italic",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: blue[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackPlayedIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },
  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
    color: theme.mode === "light" ? theme.palette.light : theme.palette.dark,
  },

  messageCenter: {
    marginTop: 5,
    alignItems: "center",
    verticalAlign: "center",
    alignContent: "center",
    backgroundColor: "#E1F5FEEB",
    fontSize: "12px",
    minWidth: 100,
    maxWidth: 270,
    color: "#272727",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  deletedMessage: {
    color: '#f55d65'
  }
}));

const reducer = (state, action) => {
  console.log("🔄 [MessagesList Reducer] Ação recebida:", action.type, "Payload:", action.payload);
  console.log("🔄 [MessagesList Reducer] Estado atual tem", state.length, "mensagens");

  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {

      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    const newState = [...newMessages, ...state];
    console.log("✅ [MessagesList Reducer] LOAD_MESSAGES completado. Novo estado tem", newState.length, "mensagens");
    return newState;
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    console.log("✅ [MessagesList Reducer] ADD_MESSAGE completado. Novo estado tem", state.length, "mensagens");
    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    console.log("✅ [MessagesList Reducer] UPDATE_MESSAGE completado");
    return [...state];
  }

  if (action.type === "DELETE_MESSAGE") {
    const messageId = action.payload;
    const newState = state.filter((m) => m.id !== messageId);
    console.log("✅ [MessagesList Reducer] DELETE_MESSAGE completado. Removeu", state.length - newState.length, "mensagem(ns)");
    return newState;
  }

  if (action.type === "RESET") {
    console.log("🔄 [MessagesList Reducer] RESET completado. Estado zerado.");
    return [];
  }
};

const MessagesList = ({
  isGroup,
  onDrop,
  whatsappId,
  queueId,
  channel,
  contactId
}) => {
  const classes = useStyles();
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const history = useHistory();
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const { ticketId } = useParams();

  const [selectionText, setSelectionText] = useState("");
  const [selectionAnchorEl, setSelectionAnchorEl] = useState(null);
  const [saveFieldOpen, setSaveFieldOpen] = useState(false);
  const selectionVirtualRef = useRef(null);

  const currentTicketId = useRef(ticketId);
  const { getAll } = useCompanySettings();
  const [dragActive, setDragActive] = useState(false);
  const [showOriginal, setShowOriginal] = useState({});
  const [transcriptions, setTranscriptions] = useState({});
  const [transcribing, setTranscribing] = useState({});

  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState(false);
  const { selectedQueuesMessage } = useContext(QueueSelectedContext);

  const { showSelectMessageCheckbox } = useContext(ForwardMessageContext);

  const { user, socket } = useContext(AuthContext);

  const companyId = user.companyId;

  useEffect(() => {

    async function fetchData() {

      const settings = await getAll(companyId);

      let settinglgpdDeleteMessage;
      let settingEnableLGPD;

      for (const [key, value] of Object.entries(settings)) {

        if (key === "lgpdDeleteMessage") settinglgpdDeleteMessage = value
        if (key === "enableLGPD") settingEnableLGPD = value
      }
      if (settingEnableLGPD === "enabled" && settinglgpdDeleteMessage === "enabled") {
        setLGPDDeleteMessage(true);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
  }, [ticketId, selectedQueuesMessage]);

  useEffect(() => {
    console.log("🔍 [MessagesList] useEffect disparado - ticketId:", ticketId, "pageNumber:", pageNumber);
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === "undefined") {
          console.log("⚠️ [MessagesList] ticketId é 'undefined', redirecionando...");
          history.push("/tickets");
          return;
        }
        if (isNil(ticketId)) {
          console.log("⚠️ [MessagesList] ticketId é nil/null, abortando...");
          return;
        }
        try {
          console.log("📡 [MessagesList] Fazendo requisição para:", "/messages/" + ticketId);
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber, selectedQueues: JSON.stringify(selectedQueuesMessage) },
          });

          console.log("✅ [MessagesList] Resposta recebida:", {
            messagesCount: data.messages?.length,
            hasMore: data.hasMore,
            ticketInfo: data.ticket
          });

          if (currentTicketId.current === ticketId) {
            console.log("📥 [MessagesList] Despachando LOAD_MESSAGES com", data.messages?.length, "mensagens");
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
            setLoadingMore(false);

            // Carregar transcrições já salvas no banco
            const savedTranscriptions = {};
            data.messages.forEach((msg) => {
              if (msg.transcription) {
                try {
                  const parsed = JSON.parse(msg.transcription);
                  savedTranscriptions[msg.id] = {
                    transcribedText: parsed.transcribedText || null,
                    translatedText: parsed.translatedText || null,
                    detectedLanguage: parsed.detectedLanguage || null,
                    showOriginal: false,
                  };
                } catch (e) {}
              }
            });
            if (Object.keys(savedTranscriptions).length > 0) {
              setTranscriptions(prev => ({ ...prev, ...savedTranscriptions }));
            }
          } else {
            console.log("⚠️ [MessagesList] ticketId mudou, ignorando resposta");
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          console.error("❌ [MessagesList] Erro ao buscar mensagens:", err);
          setLoading(false);
          toastError(err);
          setLoadingMore(false);
        }
      };

      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, selectedQueuesMessage]);

  useEffect(() => {
    if (ticketId === "undefined") {
      return;
    }

    const companyId = user.companyId;

    console.log("🔄 [MessagesList] useEffect disparado - Configurando listeners Socket.IO:", {
      ticketId,
      companyId,
      socketExists: !!socket,
      eventName: `company-${companyId}-appMessage`
    });

    // ✅ CORREÇÃO: Usar ticketId (UUID) diretamente
    const connectEventMessagesList = () => {
      console.log("🔌 [MessagesList] Conectando ao chat box:", ticketId);
      socket.emit("joinChatBox", ticketId);
    }

    const onAppMessageMessagesList = (data) => {
      console.log("🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉");
      console.log("📥 [MessagesList] EVENTO SOCKET.IO RECEBIDO!");
      console.log("🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉🎉");
      console.log("📦 [MessagesList] Payload COMPLETO:", JSON.stringify(data, null, 2));
      console.log("📥 [MessagesList] Resumo da Mensagem:", {
        action: data.action,
        ticketUuid: data.ticket?.uuid,
        messageTicketUuid: data.message?.ticket?.uuid,
        messageTicketId: data.message?.ticketId,
        messageId: data.message?.id,
        messageBody: data.message?.body,
        currentTicketId: ticketId,
        currentTicketIdType: typeof ticketId,
        ticketUuidType: typeof data.ticket?.uuid,
        matchCreate: data.action === "create" && data.ticket?.uuid === ticketId,
        matchUpdate: data.action === "update" && data?.message?.ticket?.uuid === ticketId,
        matchDelete: data.action === "delete" && data.message?.ticket?.uuid === ticketId,
        strictEquality: data.ticket?.uuid === ticketId,
        looseEquality: data.ticket?.uuid == ticketId
      });
      console.log("===========================================");

      if (data.action === "create" && data.ticket?.uuid === ticketId) {
        console.log("✅ [MessagesList] ADD_MESSAGE - Validação passou, adicionando mensagem");
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      } else if (data.action === "create") {
        console.warn("⚠️ [MessagesList] ADD_MESSAGE - Validação falhou:", {
          dataTicketUuid: data.ticket?.uuid,
          expectedTicketId: ticketId,
          areEqual: data.ticket?.uuid === ticketId
        });
      }

      if (data.action === "update" && data?.message?.ticket?.uuid === ticketId) {
        console.log("✅ [MessagesList] UPDATE_MESSAGE - Validação passou");
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }

      if (data.action == "delete" && data.message.ticket?.uuid === ticketId) {
        console.log("✅ [MessagesList] DELETE_MESSAGE - Validação passou");
        dispatch({ type: "DELETE_MESSAGE", payload: data.messageId });
      }
    }
    socket.on("connect", connectEventMessagesList);
    socket.on(`company-${companyId}-appMessage`, onAppMessageMessagesList);

    return () => {
      console.log("🔌 [MessagesList] Desconectando do chat box:", ticketId);
      socket.emit("joinChatBoxLeave", ticketId);

      socket.off("connect", connectEventMessagesList);
      socket.off(`company-${companyId}-appMessage`, onAppMessageMessagesList);
    };

  }, [ticketId]);

  const loadMore = () => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

const scrollToBottom = () => {
  setTimeout(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  }, 100);
};

const handleScroll = (e) => {
  if (!hasMore) return;
  const { scrollTop } = e.currentTarget;

  if (scrollTop === 0) {
    document.getElementById("messagesList").scrollTop = 1;
  }

  if (loading) {
    return;
  }

  if (scrollTop < 50) {
    loadMore();
  }
};

const handleTextSelection = useCallback(() => {
  const selection = window.getSelection();
  const text = selection ? selection.toString().trim() : "";

  if (text.length >= 3 && contactId) {
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    selectionVirtualRef.current = {
      getBoundingClientRect: () => rect,
      clientWidth: rect.width,
      clientHeight: rect.height,
    };
    setSelectionText(text);
    setSelectionAnchorEl(selectionVirtualRef.current);
  } else {
    setSelectionAnchorEl(null);
    setSelectionText("");
  }
}, [contactId]);

const handleOpenMessageOptionsMenu = (e, message) => {
  setAnchorEl(e.currentTarget);
  setSelectedMessage(message);
};

const handleCloseMessageOptionsMenu = (e) => {
  setAnchorEl(null);
};

const hanldeReplyMessage = (e, message) => {
  //if (ticket.status === "open" || ticket.status === "group") {
  setAnchorEl(null);
  setReplyingMessage(message);
  //}
};

const checkMessageMedia = (message) => {
  console.log(message)
  if (message.mediaType === "locationMessage" && message.body.split('|').length >= 2) {
    let locationParts = message.body.split('|')
    let imageLocation = locationParts[0]
    let linkLocation = locationParts[1]

    let descriptionLocation = null

    if (locationParts.length > 2)
      descriptionLocation = message.body.split('|')[2]

    return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
  } else

    if (message.mediaType === "contactMessage") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      // console.log(message)
      return <VcardPreview contact={contact} numbers={obj[0]?.number} queueId={message?.ticket?.queueId} whatsappId={message?.ticket?.whatsappId} />
    } else if (message.mediaType === "adMetaPreview") { // Adicionado para renderizar o componente de preview de anúncio
  let [image, sourceUrl, title, body] = message.body.split('|');
  let messageUser = i18n.t("messagesResponsive.ads.defaultUserMessage");
  return <AdMetaPreview image={image} sourceUrl={sourceUrl} title={title} body={body} messageUser={messageUser} />;
}

      if (message.mediaType === "image") {
        return <ModalImageCors imageUrl={message.mediaUrl} />;
      } else

        if (message.mediaType === "audio") {
          return (
            <>
              <AudioModal url={message.mediaUrl} />
              {renderAudioTranscription(message)}
            </>
          );
        } else

          if (message.mediaType === "video") {
            return (
              <video
                className={classes.messageMedia}
                src={message.mediaUrl}
                controls
              />
            );
          } else {
            return (
              <>
                <div className={classes.downloadMedia}>
                  <Button
                    startIcon={<GetApp />}
                    variant="outlined"
                    target="_blank"
                    href={message.mediaUrl}
                  >
                    {i18n.t("messagesResponsive.actions.download")}
                  </Button>
                </div>
                <Divider />
              </>
            );
          }
};

const renderMessageAck = (message) => {
  if (message.ack === 0) {
    return <AccessTime fontSize="small" className={classes.ackIcons} />;
  } else
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    } else
      if (message.ack === 2) {
        return <DoneAll fontSize="small" className={classes.ackIcons} />;
      } else
        if (message.ack === 3 || message.ack === 4) {
          return <DoneAll fontSize="small" className={message.mediaType === "audio" ? classes.ackPlayedIcon : classes.ackDoneAllIcon} />;
        } else
          if (message.ack === 5) {
            return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />
          }
};

const renderDailyTimestamps = (message, index) => {
  const today = format(new Date(), "dd/MM/yyyy")

  if (index === 0) {
    return (
      <span
        className={classes.dailyTimestamp}
        key={`timestamp-${message.id}`}
      >
        <div className={classes.dailyTimestampText}>
          {today === format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy") ? i18n.t("messagesResponsive.timestamp.today") : format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
        </div>
      </span>
    );
  } else
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {today === format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy") ? i18n.t("messagesResponsive.timestamp.today") : format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    } else
      if (index === messagesList.length - 1) {
        return (
          <div
            key={`ref-${message.id}`}
            ref={lastMessageRef}
            style={{ float: "left", clear: "both" }}
          />
        );
      }
};


const renderTicketsSeparator = (message, index) => {
  let lastTicket = messagesList[index - 1]?.ticketId;
  let currentTicket = message.ticketId;

  if (lastTicket !== currentTicket && lastTicket !== undefined) {
    if (message?.ticket?.queue) {
      return (
        <span
          className={classes.currentTick}
          key={`timestamp-${message.id}a`}
        >
          <div
            className={classes.currentTicktText}
            style={{ backgroundColor: message?.ticket?.queue?.color || "grey" }}
          >
            #{i18n.t("ticketsList.called")} {message?.ticketId} - {message?.ticket?.queue?.name}
          </div>

        </span>
      );
    } else {
      return (
        <span
          className={classes.currentTick}
          key={`timestamp-${message.id}b`}
        >
          <div
            className={classes.currentTicktText}
            style={{ backgroundColor: "grey" }}
          >
            #{i18n.t("ticketsList.called")} {message.ticketId} - {i18n.t("ticketsList.noQueue")}
          </div>

        </span>
      );
    }
  }

};

const renderMessageDivider = (message, index) => {
  if (index < messagesList.length && index > 0) {
    let messageUser = messagesList[index].fromMe;
    let previousMessageUser = messagesList[index - 1].fromMe;
    if (messageUser !== previousMessageUser) {
      return (

        <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
      );
    }
  }
};

const path = require('path');

// Helper para obter o corpo da mensagem (com suporte a tradução)
const getMessageBody = (message) => {
  // Mensagens recebidas (fromMe=false): translatedBody = tradução para idioma do atendente
  // Mensagens enviadas (fromMe=true): body = texto original do atendente, translatedBody = o que foi enviado traduzido
  if (message.translatedBody && !message.fromMe) {
    // Mensagem recebida traduzida: mostra tradução por padrão
    return showOriginal[message.id] ? message.body : message.translatedBody;
  }
  return message.body;
};

const toggleOriginal = (messageId) => {
  setShowOriginal(prev => ({ ...prev, [messageId]: !prev[messageId] }));
};

const handleTranscribeAudio = async (message) => {
  if (transcribing[message.id]) return;

  // Extract filename from mediaUrl
  const url = message.mediaUrl || "";
  const fileName = url.split("/").pop();
  if (!fileName) return;

  setTranscribing(prev => ({ ...prev, [message.id]: true }));
  try {
    const { data } = await api.get(`/messages/transcribeAudio/${fileName}`);
    setTranscriptions(prev => ({
      ...prev,
      [message.id]: {
        transcribedText: data.transcribedText,
        translatedText: data.translatedText,
        detectedLanguage: data.detectedLanguage,
        showOriginal: false,
      }
    }));
  } catch (err) {
    toastError(err);
  }
  setTranscribing(prev => ({ ...prev, [message.id]: false }));
};

const renderAudioTranscription = (message) => {
  const t = transcriptions[message.id];
  if (!t) {
    return (
      <span
        className={classes.transcribeBtn}
        onClick={() => handleTranscribeAudio(message)}
      >
        {transcribing[message.id] ? (
          <>
            <CircularProgress size={12} />
            {i18n.t("messagesResponsive.transcription.transcribing")}
          </>
        ) : (
          <>
            <Translate className={classes.translatedIcon} />
            {i18n.t("messagesResponsive.transcription.transcribe")}
          </>
        )}
      </span>
    );
  }

  // Mostra o texto traduzido (se disponível) ou o transcrito original
  const displayText = t.translatedText || t.transcribedText;

  return (
    <div>
      <div className={classes.transcriptionText}>
        {displayText}
      </div>
    </div>
  );
};

const renderTranslationIndicator = (message) => {
  if (!message.translatedBody) return null;

  if (!message.fromMe) {
    // Mensagem recebida traduzida
    const isShowingOriginal = showOriginal[message.id];
    const langLabel = message.originalLanguage || "?";
    return (
      <span
        className={classes.translatedIndicator}
        onClick={() => toggleOriginal(message.id)}
      >
        <Translate className={classes.translatedIcon} />
        {isShowingOriginal
          ? i18n.t("messagesResponsive.translation.showTranslation") || "Ver tradução"
          : (i18n.t("messagesResponsive.translation.translatedFrom") || "Traduzido de") + " " + langLabel}
      </span>
    );
  } else {
    // Mensagem enviada traduzida: mostrar o que foi enviado ao contato
    const isShowingTranslated = showOriginal[message.id];
    return (
      <span
        className={classes.translatedIndicator}
        onClick={() => toggleOriginal(message.id)}
      >
        <Translate className={classes.translatedIcon} />
        {isShowingTranslated
          ? i18n.t("messagesResponsive.translation.showOriginal") || "Ver original"
          : i18n.t("messagesResponsive.translation.autoTranslated") || "Tradução automática enviada"}
      </span>
    );
  }
};

const renderQuotedMessage = (message) => {

  return (
    <div
      className={clsx(classes.quotedContainerLeft, {
        [classes.quotedContainerRight]: message.fromMe,
      })}
    >
      <span
        className={clsx(classes.quotedSideColorLeft, {
          [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
        })}
      ></span>
      <div className={classes.quotedMsg}>
        {!message.quotedMsg?.fromMe && (
          <span className={classes.messageContactName}>
            {message.quotedMsg?.contact?.name}
          </span>
        )}

        {message.quotedMsg.mediaType === "audio"
          && (
            <div className={classes.downloadMedia}>
              <AudioModal url={message.quotedMsg.mediaUrl} />

              {/* <audio controls>
                  <source src={message.quotedMsg.mediaUrl} type="audio/mp3"></source>
                  {/* <source src={message.quotedMsg.mediaUrl} type="audio/ogg"></source> 
                </audio> */}
            </div>
          )
        }
        {message.quotedMsg.mediaType === "video"
          && (
            <video
              className={classes.messageMedia}
              src={message.quotedMsg.mediaUrl}
              controls
            />
          )
        }
        {message.quotedMsg.mediaType === "contactMessage"
          && (
            i18n.t("messagesResponsive.types.contact")
          )
        }
        {message.quotedMsg.mediaType === "application"
          && (
            <div className={classes.downloadMedia}>
              <Button
                startIcon={<GetApp />}
                // color="primary"
                variant="outlined"
                target="_blank"
                href={message.quotedMsg.mediaUrl}
              >
                {i18n.t("messagesResponsive.actions.download")}
              </Button>
            </div>
          )
        }

        {message.quotedMsg.mediaType === "image"
          && (
            <ModalImageCors imageUrl={message.quotedMsg.mediaUrl} />)
          || message.quotedMsg?.body}

        {!message.quotedMsg.mediaType === "image" && message.quotedMsg?.body}


      </div>
    </div>
  );
};

const handleDrag = event => {
  event.preventDefault();
  event.stopPropagation();
  if (event.type === "dragenter" || event.type === "dragover") {
    setDragActive(true);
  } else if (event.type === "dragleave") {
    setDragActive(false);
  }
}

const isYouTubeLink = (url) => {
  const youtubeRegex = /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  return youtubeRegex.test(url);
};

const handleDrop = event => {
  event.preventDefault();
  event.stopPropagation();
  setDragActive(false);
  if (event.dataTransfer.files && event.dataTransfer.files[0]) {
    if (onDrop) {
      onDrop(event.dataTransfer.files);
    }
  }
}
const xmlRegex = /<([^>]+)>/g;
const boldRegex = /\*(.*?)\*/g;

const formatXml = (xmlString) => {
  // Verifica se o XML contém a assinatura com nome do atendente
  if (boldRegex.test(xmlString)) {
    // Formata o texto dentro da assinatura em negrito
    xmlString = xmlString.replace(boldRegex, "**$1**");
  }
  return xmlString;
};

const renderMessages = () => {
  console.log("🎨 [MessagesList] renderMessages chamado. messagesList.length:", messagesList.length);

  if (messagesList.length > 0) {
    console.log("🎨 [MessagesList] Renderizando", messagesList.length, "mensagens");
    const viewMessagesList = messagesList.map((message, index) => {
      if (message.mediaType === "call_log") {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderTicketsSeparator(message, index)}
            {renderMessageDivider(message, index)}
            <div className={classes.messageCenter}>
              <IconButton
                variant="contained"
                size="small"
                id="messageActionsButton"
                disabled={message.isDeleted}
                className={classes.messageActionsButton}
                onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
              >
                <ExpandMore />
              </IconButton>
              {isGroup && (
                <span className={classes.messageContactName}>
                  {message.contact?.name}
                </span>
              )}

              {/* {isGroup && (
                  <span className={classes.messageContactName}>
                    {JSON.parse(message.dataJson).pushName} #{message.contact?.name}
                  </span>
                )} */}
              <div>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                  <path fill="#df3333" d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path>
                </svg> <span>{i18n.t("ticketsList.missedCall")} {format(parseISO(message.createdAt), "HH:mm")}</span>
              </div>
            </div>
          </React.Fragment>
        );
      }

      if (!message.fromMe) {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderTicketsSeparator(message, index)}
            {renderMessageDivider(message, index)}
            <div
              className={classes.messageLeft}
              title={message.queueId && message.queue?.name}
              onDoubleClick={(e) => hanldeReplyMessage(e, message)}
            >
              {showSelectMessageCheckbox && (
                <SelectMessageCheckbox
                  // showSelectMessageCheckbox={showSelectMessageCheckbox}
                  message={message}
                // selectedMessagesList={selectedMessagesList}
                // setSelectedMessagesList={setSelectedMessagesList}
                />
              )}
              <IconButton
                variant="contained"
                size="small"
                id="messageActionsButton"
                disabled={message.isDeleted}
                className={classes.messageActionsButton}
                onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
              >
                <ExpandMore />
              </IconButton>

              {message.isForwarded && (
                <div>
                  <span className={classes.forwardMessage}
                  ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> {i18n.t("messagesResponsive.status.forwarded")}
                  </span>
                  <br />
                </div>
              )}
              {isGroup && (
                <span className={classes.messageContactName}>
                  {message.contact?.name}
                </span>
              )}
              {isYouTubeLink(message.body) && (
                <>
                  <YouTubePreview videoUrl={message.body} />
                </>
              )}
              {/* {isGroup && (
                  <span className={classes.messageContactName}>
                    {JSON.parse(message.dataJson).pushName} #{message.contact?.name}
                  </span>
                )} */}

              {/* aviso de mensagem apagado pelo contato */}

              {!lgpdDeleteMessage && message.isDeleted && (
                <div>
                  <span className={classes.deletedMessage}
                  >{i18n.t("messagesResponsive.status.deletedByContact")} &nbsp;
                  </span>
                </div>
              )}

               {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage" || message.mediaType === "template" || message.mediaType === "adMetaPreview" // Adicionado para aceitar o componente de preview de anúncio
                //|| message.mediaType === "multi_vcard" 
              ) && checkMessageMedia(message)}

              <div className={clsx(classes.textContentItem, {
                [classes.textContentItemDeleted]: message.isDeleted,
              })}>
                {message.quotedMsg && renderQuotedMessage(message)}
                {
  message.mediaType !== "adMetaPreview" && (
    (
      (message.mediaUrl !== null && (message.mediaType === "image" || message.mediaType === "video") && path.basename(message.mediaUrl).trim() !== message.body.trim()) ||
      message.mediaType !== "audio" &&
      message.mediaType !== "image" &&
      message.mediaType !== "video" &&
      message.mediaType != "reactionMessage" &&
      message.mediaType != "locationMessage" &&
      message.mediaType !== "contactMessage"
    ) && (
      <>
        {xmlRegex.test(message.body) && (
          <span>{getMessageBody(message)}</span>
        )}
        {!xmlRegex.test(message.body) && (
          <MarkdownWrapper>{(lgpdDeleteMessage && message.isDeleted) ? i18n.t("messagesResponsive.status.deletedMessage") : getMessageBody(message)}</MarkdownWrapper>
        )}
        {renderTranslationIndicator(message)}
      </>
    )
  )
}


                {message.quotedMsg && message.mediaType === "reactionMessage" && (
                  <>
                    <span style={{ marginLeft: "0px" }}>
                      <MarkdownWrapper>
                        {"" + message?.contact?.name + i18n.t("messagesResponsive.reactions.contactReacted") + message.body}
                      </MarkdownWrapper>
                    </span>
                  </>
                )}

                <span className={classes.timestamp}>
                  {message.isEdited ? i18n.t("messagesResponsive.status.edited") + " " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                </span>
              </div>
            </div>
          </React.Fragment>
        );
      } else {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderTicketsSeparator(message, index)}
            {renderMessageDivider(message, index)}
            <div
              className={message.isPrivate ? classes.messageRightPrivate : classes.messageRight}
              title={message.queueId && message.queue?.name}
              onDoubleClick={(e) => hanldeReplyMessage(e, message)}
            >
              {showSelectMessageCheckbox && (
                <SelectMessageCheckbox
                  // showSelectMessageCheckbox={showSelectMessageCheckbox}
                  message={message}
                // selectedMessagesList={selectedMessagesList}
                // setSelectedMessagesList={setSelectedMessagesList}
                />
              )}

              <IconButton
                variant="contained"
                size="small"
                id="messageActionsButton"
                disabled={message.isDeleted}
                className={classes.messageActionsButton}
                onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
              >
                <ExpandMore />
              </IconButton>
              {message.isForwarded && (
                <div>
                  <span className={classes.forwardMessage}
                  ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> {i18n.t("messagesResponsive.status.forwarded")}
                  </span>
                  <br />
                </div>
              )}
              {isYouTubeLink(message.body) && (
                <>
                  <YouTubePreview videoUrl={message.body} />
                </>
              )}
              {!lgpdDeleteMessage && message.isDeleted && (
                <div>
                  <span className={classes.deletedMessage}
                  >{i18n.t("messagesResponsive.status.deletedByMe")} &nbsp;
                  </span>
                </div>
              )}
              {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage" || message.mediaType === "template" || message.mediaType === "adMetaPreview" // Adicionado para aceitar o componente de preview de anúncio
                //|| message.mediaType === "multi_vcard" 
              ) && checkMessageMedia(message)}
              <div
                className={clsx(classes.textContentItem, {
                  [classes.textContentItemDeleted]: message.isDeleted,
                })}
              >

                {/* {message.isDeleted && (`🚫`)} */}



                {message.quotedMsg && renderQuotedMessage(message)}

                {
                  ((message.mediaType === "image" || message.mediaType === "video") && path.basename(message.mediaUrl) === message.body) ||
                  (message.mediaType !== "audio" && message.mediaType != "reactionMessage" && message.mediaType != "locationMessage" && message.mediaType !== "contactMessage") && (
                    <>
                      {xmlRegex.test(message.body) && (
                        <div>{formatXml(
                          message.translatedBody
                            ? (!message.fromMe
                              ? (showOriginal[message.id] ? message.body : message.translatedBody)
                              : (showOriginal[message.id] ? message.translatedBody : message.body))
                            : message.body
                        )}</div>

                      )}
                      {!xmlRegex.test(message.body) && (<MarkdownWrapper>{
                        message.translatedBody
                          ? (!message.fromMe
                            ? (showOriginal[message.id] ? message.body : message.translatedBody)
                            : (showOriginal[message.id] ? message.translatedBody : message.body))
                          : message.body
                      }</MarkdownWrapper>)}
                      {renderTranslationIndicator(message)}
                    </>
                  )}

                {message.quotedMsg && message.mediaType === "reactionMessage" && (
                  <>
                    <span style={{ marginLeft: "0px" }}>
                      <MarkdownWrapper>
                        {i18n.t("messagesResponsive.reactions.youReacted") + message.body}
                      </MarkdownWrapper>
                    </span>
                  </>
                )}

                <span className={classes.timestamp}>
                  {message.isEdited ? i18n.t("messagesResponsive.status.edited") + " " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                  {renderMessageAck(message)}
                </span>
              </div>
            </div>
          </React.Fragment>
        );
      }
    });
    return viewMessagesList;
  } else {
    return <div>{i18n.t("messagesResponsive.placeholder.sayHello")}</div>;
  }
};

return (
  <div className={classes.messagesListWrapper} onDragEnter={handleDrag}>
    {dragActive && <div className={classes.dragElement} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>{i18n.t("messagesResponsive.actions.dropFileHere")}</div>}

    <MessageOptionsMenu
      message={selectedMessage}
      anchorEl={anchorEl}
      menuOpen={messageOptionsMenuOpen}
      handleClose={handleCloseMessageOptionsMenu}
      isGroup={isGroup}
      whatsappId={whatsappId}
      queueId={queueId}
      contactId={contactId}
    />
    <div
      id="messagesList"
      className={classes.messagesList}
      onScroll={handleScroll}
      onMouseUp={handleTextSelection}
    >
      {messagesList.length > 0 ?
        renderMessages()
        : []}
    </div>

    {Boolean(selectionAnchorEl) && selectionText && (
      <Popper
        open={true}
        anchorEl={selectionAnchorEl}
        placement="top"
        style={{ zIndex: 9999 }}
      >
        <ClickAwayListener onClickAway={() => { setSelectionAnchorEl(null); setSelectionText(""); }}>
          <Paper
            className={classes.saveFieldFab}
            onClick={() => { setSaveFieldOpen(true); setSelectionAnchorEl(null); }}
          >
            <SaveAltIcon style={{ fontSize: 16 }} />
            Salvar no contato
          </Paper>
        </ClickAwayListener>
      </Popper>
    )}

    <SaveFieldFromTextMenu
      open={saveFieldOpen}
      onClose={() => { setSaveFieldOpen(false); setSelectionText(""); }}
      selectedText={selectionText}
      contactId={contactId}
    />

    {(channel !== "whatsapp" && channel !== undefined) && (
      <div
        style={{
          width: "100%",
          display: "flex",
          padding: "10px",
          alignItems: "center",
          backgroundColor: "#E1F3FB",
        }}
      >
        {channel === "facebook" ? (
          <Facebook />
        ) : (
          <Instagram />
        )}

        <span>
          {i18n.t("messagesResponsive.warnings.facebookPolicy")}
        </span>
      </div>
    )}
    {loading && (
      <div>
        <CircularProgress className={classes.circleLoading} />
      </div>
    )}
  </div>
);
};

export default MessagesList;